import { useEffect, useState } from 'react'

// Dismissable receptionist motivator popup (Paul 2026-07-29). Record-chasing nudge — today's bookings
// SO FAR (split new vs existing) vs the all-time RECORD for this weekday, framed as a personal-best to
// beat, never a deficit/quota.
// 2026-08-24 (Paul): added the PACE line — today vs what a normal <weekday> already has on the board BY
// THIS TIME — so reception can measure herself against her own usual rhythm, not only the all-time best.
// The pace line is dropped entirely when the backend has too little history to be honest about it.
// 2026-08-25 (Paul): shows ALL DAY, not only inside the 10/12/14 slot windows — "we used to always have
// it". Dismissal is now per-DAY (the ✕ hides it for today, it's back tomorrow) instead of per-slot.
export default function ReceptionMotivator() {
  const [data, setData] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetch('/analytics/reception_motivator', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return
        const key = `rm_${new Date().toISOString().slice(0, 10)}` // dismissed for the whole day, if at all
        try {
          if (localStorage.getItem(key)) return // already dismissed today
        } catch (_) {
          /* private window / storage blocked — just show it */
        }
        setData({ ...d, key })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  if (!data) return null

  const close = () => {
    try {
      localStorage.setItem(data.key, '1')
    } catch (_) {
      /* ignore */
    }
    setData(null)
  }

  const emoji =
    data.status === 'record_beaten' ? '\u{1F3C6}' : data.status === 'record_tied' ? '\u{1F525}' : '\u{1F4AA}'
  // The whole chase is on NEW patients (Paul 2026-08-25) — existing is shown below for context only.
  const nP = (n) => `${n} new patient${n === 1 ? '' : 's'}`
  const line =
    data.status === 'record_beaten'
      ? `New record! You've booked ${nP(data.new_today)} — the most NEW patients we've EVER booked on a ${data.weekday}! 🎉`
      : data.status === 'record_tied'
        ? `${nP(data.new_today)} — that TIES our all-time ${data.weekday} NEW-patient record. One more makes history!`
        : `You've booked ${nP(data.new_today)} so far. Our best ${data.weekday} ever is ${data.record} new${
            data.record_date ? ` (${data.record_date})` : ''
          } — ${data.to_beat} more to beat it!`

  // Today vs a normal <weekday> BY THIS TIME — also new-vs-new. Always ground to gain, never a shortfall.
  const pace =
    data.pace_average == null
      ? null
      : data.pace_status === 'ahead'
        ? `⚡ Ahead of a normal ${data.weekday} — by ${data.time} you'd usually have ${data.pace_average} new.`
        : data.pace_status === 'level'
          ? `Bang on your usual ${data.weekday} new-patient pace — ${data.pace_average} by ${data.time}.`
          : `A normal ${data.weekday} has ${data.pace_average} new by ${data.time} — ${data.pace_to_beat} more and you're ahead of your own pace.`

  return (
    <div className="mb-4 flex items-start gap-3 rounded-xl border border-brand-border bg-white px-4 py-3 shadow-sm">
      <span className="text-2xl leading-none">{emoji}</span>
      <div className="flex-1 text-sm text-brand-ink">
        <div className="font-semibold">New patients booked today</div>
        <div>{line}</div>
        {pace && <div className="mt-0.5 text-brand-muted">{pace}</div>}
        <div className="mt-1 text-xs text-brand-muted">
          For context: <span className="font-medium text-brand-ink">{data.existing_today}</span> existing patient
          {data.existing_today === 1 ? '' : 's'} also booked today.
        </div>
      </div>
      <button
        type="button"
        onClick={close}
        aria-label="Dismiss"
        className="rounded p-1 text-brand-muted hover:bg-brand-border/40 hover:text-brand-ink"
      >
        {'✕'}
      </button>
    </div>
  )
}
